import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { getHomeFaqs } from '../utils/seoContent';
import { useLanguage } from '../LanguageContext';

const SITE_URL = 'https://www.valleyintlsupply.com';
const SITE_NAME = 'Valley International Supply';
const DEFAULT_IMAGE = `${SITE_URL}/logo.png`;

const staticPages = {
  '/': {
    title: 'Industrial Automation Distributor | Valley International Supply',
    description: 'Source industrial automation, controls, sensors and pneumatic components with responsive technical support across the USA, Mexico and Central America.'
  },
  '/about': {
    title: 'About Our Industrial Supply Team | Valley International Supply',
    description: 'Meet Valley International Supply and learn how our regional teams support industrial product sourcing, selection, quoting and fulfillment across the Americas.'
  },
  '/brands': {
    title: 'Industrial Automation Brands | Valley International Supply',
    description: 'Explore industrial automation brands for controls, sensing, pneumatics, motion, machine vision, electrical protection and industrial connectivity.'
  },
  '/info': {
    title: 'Industrial Automation Solutions & Applications | Valley',
    description: 'Explore practical industrial automation solutions for factory control, robotics, Industry 4.0, PLC systems, sensing and machine safety.'
  },
  '/terms': {
    title: 'Terms and Conditions | Valley International Supply',
    description: 'Review the terms and conditions that apply when using the Valley International Supply website and purchasing industrial products.'
  },
  '/privacy': {
    title: 'Privacy Policy | Valley International Supply',
    description: 'Learn how Valley International Supply collects, uses, stores and protects information when you browse products, create an account, request a quote or place an order.'
  },
  '/signup': {
    title: 'Customer Account & Sign In | Valley International Supply',
    description: 'Sign in or create a Valley International Supply customer account to manage carts, quotes, orders and industrial product purchases.',
    noindex: true
  },
  '/cart': {
    title: 'Your Industrial Products Cart | Valley International Supply',
    description: 'Review the industrial products in your Valley International Supply shopping cart.',
    noindex: true
  },
  '/orders': {
    title: 'Your Orders | Valley International Supply',
    description: 'Review and manage your Valley International Supply orders.',
    noindex: true
  },
  '/verify-email': {
    title: 'Verify Your Email | Valley International Supply',
    description: 'Verify the email address associated with your Valley International Supply account.',
    noindex: true
  },
  '/forgot-password': {
    title: 'Reset Account Access | Valley International Supply',
    description: 'Request a secure password reset for your Valley International Supply account.',
    noindex: true
  },
  '/reset-password': {
    title: 'Choose a New Password | Valley International Supply',
    description: 'Choose a new password for your Valley International Supply account.',
    noindex: true
  },
  '/thank-you': {
    title: 'Thank You | Valley International Supply',
    description: 'Your request has been received by Valley International Supply.',
    noindex: true
  }
};

const spanishStaticPages = {
  '/': {
    title: 'Distribuidor de automatización industrial | Valley International Supply',
    description: 'Obtenga productos de automatización industrial, controles, sensores y componentes neumáticos con soporte técnico ágil en Estados Unidos, México y Centroamérica.'
  },
  '/about': {
    title: 'Nuestro equipo de suministro industrial | Valley International Supply',
    description: 'Conozca cómo nuestros equipos regionales apoyan la selección, cotización y entrega de productos industriales en todo el continente americano.'
  },
  '/brands': {
    title: 'Marcas de automatización industrial | Valley International Supply',
    description: 'Explore marcas de automatización industrial para control, detección, neumática, movimiento, visión y conectividad industrial.'
  },
  '/info': {
    title: 'Soluciones y aplicaciones de automatización industrial | Valley',
    description: 'Explore soluciones prácticas de automatización industrial para control de fábricas, robótica, Industria 4.0, PLC, detección y seguridad.'
  }
};

function readableSegment(value) {
  try {
    return decodeURIComponent(value).replace(/[-_]+/g, ' ').trim();
  } catch {
    return value.replace(/[-_]+/g, ' ').trim();
  }
}

function getPageSeo(pathname, language = 'en') {
  if (language === 'es' && spanishStaticPages[pathname]) return spanishStaticPages[pathname];
  if (staticPages[pathname]) return staticPages[pathname];

  if (pathname.startsWith('/products/')) {
    const product = readableSegment(pathname.split('/').pop());
    return {
      title: `${product} | Industrial Parts | Valley Supply`,
      description: `View availability, product details and related industrial components for ${product}. Request pricing and technical support from Valley International Supply.`
    };
  }

  if (pathname.startsWith('/brands/')) {
    const brand = readableSegment(pathname.split('/').pop());
    const brandLabel = /^\d+$/.test(brand) ? 'Industrial Manufacturer' : brand;
    return {
      title: `${brandLabel} Products | Valley International Supply`,
      description: `Browse ${brandLabel} industrial products and request availability, pricing or technical assistance from Valley International Supply.`
    };
  }

  if (pathname === '/search') {
    return {
      title: 'Search Industrial Products | Valley International Supply',
      description: 'Search industrial automation products by part number, product name, brand or category.',
      noindex: true,
      follow: true
    };
  }

  if (pathname.startsWith('/checkout/')) {
    return {
      title: 'Secure Checkout | Valley International Supply',
      description: 'Complete secure payment for your Valley International Supply order.',
      noindex: true
    };
  }

  if (pathname.startsWith('/orders/')) {
    return {
      title: 'Order Details | Valley International Supply',
      description: 'Review the details and status of your Valley International Supply order.',
      noindex: true
    };
  }

  if (pathname.startsWith('/admin') || pathname.startsWith('/control')) {
    return {
      title: `Secure Portal | ${SITE_NAME}`,
      description: 'Authorized Valley International Supply portal.',
      noindex: true
    };
  }

  return {
    title: `Page Not Found | ${SITE_NAME}`,
    description: 'The requested page could not be found. Explore industrial products and resources from Valley International Supply.',
    noindex: true
  };
}

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement('meta');
    document.head.appendChild(element);
  }
  Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
}

function upsertCanonical(href) {
  let element = document.head.querySelector('link[rel="canonical"]');
  if (!element) {
    element = document.createElement('link');
    element.setAttribute('rel', 'canonical');
    document.head.appendChild(element);
  }
  element.setAttribute('href', href);
}

function setLocalBusinessSchema(enabled, language) {
  const schemaId = 'valley-local-business-schema';
  const existing = document.getElementById(schemaId);
  document.getElementById('valley-static-faq-schema')?.remove();

  if (!enabled) {
    existing?.remove();
    return;
  }

  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': ['LocalBusiness', 'Organization'],
        '@id': `${SITE_URL}/#localbusiness`,
        name: SITE_NAME,
        url: `${SITE_URL}/`,
        image: DEFAULT_IMAGE,
        logo: DEFAULT_IMAGE,
        description: language === 'es'
          ? 'Distribuidor de equipos de automatización industrial y socio de abastecimiento para fabricantes en todo el continente americano.'
          : 'Industrial automation equipment distributor and sourcing partner serving manufacturers across the Americas.',
        telephone: '+1-956-338-0631',
        email: 'inquiry@valleyintlsupply.com',
        address: {
          '@type': 'PostalAddress',
          streetAddress: '1474 W. Price Rd., Ste. 107',
          addressLocality: 'Brownsville',
          addressRegion: 'TX',
          postalCode: '78520',
          addressCountry: 'US'
        },
        areaServed: ['United States', 'Mexico', 'Central America'],
        openingHoursSpecification: [{
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
          opens: '07:00',
          closes: '18:00'
        }],
        sameAs: ['https://www.linkedin.com/company/valley-international-supply/'],
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `${SITE_URL}/`
        }
      },
      {
        '@type': 'FAQPage',
        '@id': `${SITE_URL}/#faq`,
        mainEntity: getHomeFaqs(language).map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer
          }
        }))
      }
    ]
  };

  const element = existing || document.createElement('script');
  element.id = schemaId;
  element.type = 'application/ld+json';
  element.textContent = JSON.stringify(schema);
  if (!existing) document.head.appendChild(element);
}

function setWebPageSchema(seo, canonical, language) {
  const schemaId = 'valley-webpage-schema';
  let element = document.getElementById(schemaId);

  if (seo.noindex) {
    element?.remove();
    return;
  }

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${canonical}#webpage`,
    url: canonical,
    name: seo.title,
    description: seo.description,
    inLanguage: language === 'es' ? 'es' : 'en-US',
    isPartOf: {
      '@type': 'WebSite',
      '@id': `${SITE_URL}/#website`,
      name: SITE_NAME,
      url: `${SITE_URL}/`
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: `${SITE_URL}/`
    }
  };

  element = element || document.createElement('script');
  element.id = schemaId;
  element.type = 'application/ld+json';
  element.textContent = JSON.stringify(schema);
  if (!element.parentNode) document.head.appendChild(element);
}

export default function SeoManager() {
  const { pathname } = useLocation();
  const { language } = useLanguage();

  useEffect(() => {
    const seo = getPageSeo(pathname, language);
    const canonical = `${SITE_URL}${pathname === '/' ? '/' : pathname.replace(/\/$/, '')}`;
    const robots = seo.noindex
      ? `noindex, ${seo.follow ? 'follow' : 'nofollow'}`
      : 'index, follow, max-image-preview:large';

    document.title = seo.title;
    document.documentElement.lang = language;
    upsertCanonical(canonical);
    upsertMeta('meta[name="description"]', { name: 'description', content: seo.description });
    upsertMeta('meta[name="robots"]', { name: 'robots', content: robots });
    upsertMeta('meta[property="og:title"]', { property: 'og:title', content: seo.title });
    upsertMeta('meta[property="og:description"]', { property: 'og:description', content: seo.description });
    upsertMeta('meta[property="og:url"]', { property: 'og:url', content: canonical });
    upsertMeta('meta[property="og:type"]', { property: 'og:type', content: 'website' });
    upsertMeta('meta[property="og:image"]', { property: 'og:image', content: DEFAULT_IMAGE });
    upsertMeta('meta[property="og:site_name"]', { property: 'og:site_name', content: SITE_NAME });
    upsertMeta('meta[name="twitter:card"]', { name: 'twitter:card', content: 'summary_large_image' });
    upsertMeta('meta[name="twitter:title"]', { name: 'twitter:title', content: seo.title });
    upsertMeta('meta[name="twitter:description"]', { name: 'twitter:description', content: seo.description });
    upsertMeta('meta[name="twitter:image"]', { name: 'twitter:image', content: DEFAULT_IMAGE });
    setLocalBusinessSchema(pathname === '/', language);
    setWebPageSchema(seo, canonical, language);
  }, [language, pathname]);

  return null;
}
